/**
 * React 19 removed `ReactDOM.findDOMNode`. Several third-party dependencies we still depend on
 * (react-transition-group, react-select-v1, react-onclickoutside, @pdfme/ui's bundled rc-* deps)
 * call it internally and crash with `findDOMNode is not a function` under React 19.
 *
 * This module re-implements `findDOMNode` on top of React 19's still-present fiber internals and
 * installs it back onto the `react-dom` module export, so those dependencies keep working without
 * downgrading React. It intentionally mirrors the historical `findDOMNode` contract:
 *   - a DOM node is returned unchanged
 *   - a mounted class-component instance is resolved to its first host (DOM) node
 *   - `null` / unmounted / null-rendering components return `null`
 *
 * This is a deliberate, scoped use of React internals to bridge legacy libraries during the
 * React 19 migration; new code must never call `findDOMNode`.
 */

// React fiber tags for host nodes (stable across React 18/19): 5 = HostComponent, 6 = HostText.
const HOST_COMPONENT = 5;
const HOST_TEXT = 6;

type Fiber = {
    tag: number;
    stateNode: unknown;
    child: Fiber | null;
    sibling: Fiber | null;
    return: Fiber | null;
};

type MaybeReactInstance =
    | {
          _reactInternals?: Fiber;
          _reactInternalFiber?: Fiber;
      }
    | null
    | undefined;

const findHostNode = (fiber: Fiber): Node | null => {
    let node: Fiber | null = fiber.child;

    while (node) {
        if (node.tag === HOST_COMPONENT || node.tag === HOST_TEXT) {
            return node.stateNode as Node;
        }

        if (node.child) {
            node = node.child;
            continue;
        }

        if (node === fiber) {
            return null;
        }

        while (!node.sibling) {
            if (!node.return || node.return === fiber) {
                return null;
            }
            node = node.return;
        }

        node = node.sibling;
    }

    return null;
};

/**
 * Re-implementation of the historical `ReactDOM.findDOMNode`.
 *
 * @param componentOrElement a DOM node or a mounted class-component instance
 * @returns the corresponding host DOM node, or `null` if none can be resolved
 */
export const findDOMNode = (componentOrElement: unknown): Node | Element | null => {
    if (componentOrElement === null || componentOrElement === undefined) {
        return null;
    }

    // Already a DOM node.
    if (typeof (componentOrElement as Node).nodeType === 'number') {
        return componentOrElement as Node;
    }

    const fiber =
        (componentOrElement as NonNullable<MaybeReactInstance>)._reactInternals ??
        (componentOrElement as NonNullable<MaybeReactInstance>)._reactInternalFiber;

    if (!fiber) {
        return null;
    }

    return findHostNode(fiber);
};

/**
 * Installs {@link findDOMNode} back onto the `react-dom` module export if React removed it.
 *
 * We mutate the value obtained through a CommonJS `require('react-dom')` on purpose: in
 * webpack/rspack that is the single shared `module.exports` object that every bundled consumer
 * (including those using `import {findDOMNode} from 'react-dom'`, which read it live at call time)
 * resolves to. Call this once, as early as possible in each entrypoint, before anything renders.
 */
export const installFindDOMNodeShim = (): void => {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const reactDom = require('react-dom') as {findDOMNode?: typeof findDOMNode};

    if (typeof reactDom.findDOMNode !== 'function') {
        reactDom.findDOMNode = findDOMNode;
    }
};
